import { usePermissionsStore } from "@/stores/permissions";
import { useAuthStore } from "@/stores/auth";
import {
  checkPermissions,
  checkSubscriptionPermission,
  isSponsoredRole,
} from "@/lib/permissions";

/**
 * True when the user holds every required RBAC permission code.
 * Denies access until RBAC permissions have loaded.
 */
export function useHasPermission(required: string[]): boolean {
  const permissions = usePermissionsStore((s) => s.permissions);
  const permissionsLoaded = usePermissionsStore((s) => s.permissionsLoaded);
  if (!permissionsLoaded) return false;
  return checkPermissions(required, permissions);
}

/**
 * True when the active subscription includes the given plan feature code.
 * Sponsored users rely on their resource owner's plan. Other users need a
 * loaded, explicit grant unless no feature is required.
 *
 * Pass `count` to also enforce addon usage limits (pass -1 to skip limit
 * checks, matching the legacy `-1` sentinel).
 */
export function useHasPlanFeature(feature?: string, count?: number): boolean {
  const role = useAuthStore((s) => s.user?.role);
  const subscriptionPermissions = usePermissionsStore(
    (s) => s.subscriptionPermissions,
  );
  const addonPermissions = usePermissionsStore((s) => s.addonPermissions);
  const subscriptionLoaded = usePermissionsStore((s) => s.subscriptionLoaded);
  if (!feature || isSponsoredRole(role)) return true;
  if (!subscriptionLoaded) return false;
  return checkSubscriptionPermission(
    feature,
    subscriptionPermissions,
    count,
    addonPermissions,
  );
}
